/**
 * useUserRoleAssignments
 * Fetch tất cả role assignments của user hiện tại từ user_role_assignments
 * Mỗi assignment có: role_code, project_id (null = global), project_name
 *
 * Khi user có nhiều vai trò (kiêm nhiệm), hook này trả về toàn bộ để
 * MultiRoleDashboard render tab động theo vai trò thực tế.
 */
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/core/database/supabase';
import type { UserRole } from '@/config/navigation.config';

export interface RoleAssignment {
  id: string;
  roleCode: UserRole;
  roleName: string;
  projectId: string | null;
  projectName: string | null;
  /** true = role toàn công ty, false = chỉ trong dự án cụ thể */
  isGlobal: boolean;
}

export interface UseUserRoleAssignmentsReturn {
  assignments: RoleAssignment[];
  /** Tất cả role codes duy nhất (deduped) */
  allRoleCodes: UserRole[];
  /** Các projectIds user được gán (không null) */
  scopedProjectIds: string[];
  /** User có nhiều vai trò không */
  isMultiRole: boolean;
  loading: boolean;
  error: string | null;
}

export function useUserRoleAssignments(userId: string | null): UseUserRoleAssignmentsReturn {
  const [assignments, setAssignments] = useState<RoleAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssignments = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('user_roles')
        .select(`
          role_code,
          roles (name)
        `)
        .eq('user_id', userId);

      if (err) throw err;

      const mapped: RoleAssignment[] = (data ?? []).map((row: any, idx: number) => {
        const roleObj = Array.isArray(row.roles) ? row.roles[0] : row.roles;
        return {
          id: `role_${idx}`,
          roleCode: row.role_code as UserRole,
          roleName: roleObj?.name ?? row.role_code ?? '',
          projectId: null,
          projectName: null,
          isGlobal: true,
        };
      });

      setAssignments(mapped);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Lỗi tải role assignments');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const allRoleCodes = [...new Set(assignments.map(a => a.roleCode))];
  const scopedProjectIds = [...new Set(assignments.filter(a => a.projectId).map(a => a.projectId as string))];
  const isMultiRole = allRoleCodes.length > 1;

  return {
    assignments,
    allRoleCodes,
    scopedProjectIds,
    isMultiRole,
    loading,
    error,
  };
}
